import React, { useEffect, useState } from "react";
import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import donorUser from "../../assets/Images/TempleDetail/donor-user.svg";
import { getRecentDonorsByCampaign, getTopDonorsByCampaign } from "../../api/services/donorService";
import { T, FONT, SectionCard, SectionHeading } from "./shared";
import { MobileHeading } from "./mobile/shared";
import donorMobile from "../../assets/Images/TempleDetail/mobile/donor.svg";

const amountLabel = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  if (!currency || currency === "INR") return `₹${n.toLocaleString("en-IN")}`;
  return `${currency} ${n.toLocaleString("en-US")}`;
};

// Figma 3486:6073 "Donors — Card Grid", with the Top donors / Recent toggle.
const TempleDonors = ({ campaignId, onTopDonors }) => {
  // Phones follow Figma "Mobile revamp" 739:17578
  const phone = useMediaQuery("(max-width:600px)");
  const [tab, setTab] = useState("top");
  const [donors, setDonors] = useState({ top: null, recent: null });

  useEffect(() => {
    if (!campaignId || donors[tab]) return;
    let cancelled = false;
    const load = tab === "top" ? getTopDonorsByCampaign(campaignId) : getRecentDonorsByCampaign(campaignId, 8);
    load
      .then((rows) => {
        if (cancelled) return;
        const list = (Array.isArray(rows) ? rows : []).slice(0, 8);
        setDonors((d) => ({ ...d, [tab]: list }));
        if (tab === "top") onTopDonors?.(list);
      })
      .catch(() => !cancelled && setDonors((d) => ({ ...d, [tab]: [] })));
    return () => {
      cancelled = true;
    };
  }, [campaignId, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = donors[tab];
  if (donors.top && donors.top.length === 0 && donors.recent !== null && donors.recent.length === 0) return null;

  const toggle = (key, label) => (
    <Box
      component="button"
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      sx={{
        px: phone ? "14px" : "16px",
        py: phone ? "8px" : "8px",
        border: "none",
        borderRadius: "100px",
        background: tab === key ? T.red : "transparent",
        color: tab === key ? "#FFFFFF" : T.muted,
        cursor: "pointer",
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: "12px",
        lineHeight: "16px",
        whiteSpace: "nowrap",
        transition: "background 0.2s, color 0.2s",
        "&:focus-visible": { outline: `2px solid ${T.ink}`, outlineOffset: "2px" },
      }}
    >
      {label}
    </Box>
  );

  return (
    <SectionCard id="donors" sx={{ display: "flex", flexDirection: "column", gap: phone ? "20px" : "22px", ...(phone && { borderRadius: "20px", boxShadow: "0px 8px 24px 0px rgba(89,38,13,0.06)" }) }}>
      {/* Phones: toggle sits to the right of the heading on the same row (mobile design) */}
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: phone ? "center" : "flex-end", justifyContent: "space-between", gap: phone ? "12px" : "16px", flexWrap: phone ? "nowrap" : "wrap" }}>
        {phone ? (
          <MobileHeading eyebrow="Donors" hindi="॥ दानदाता ॥" title="Our donors" />
        ) : (
          <SectionHeading eyebrow="Donors" hindi="॥ दानदाता ॥" title="Our donors" />
        )}
        <Box role="tablist" sx={{ display: "flex", flexShrink: 0, p: phone ? "3px" : "4px", borderRadius: "100px", background: T.cream, border: `1px solid ${T.sand}` }}>
          {toggle("top", "Top donors")}
          {toggle("recent", "Recent")}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: "12px" }}>
        {list === null
          ? Array.from({ length: 8 }, (_, i) => <Skeleton key={i} variant="rounded" height={phone ? 106 : 84} sx={{ borderRadius: "14px" }} />)
          : list.map((donor, i) =>
              phone ? (
                // Mobile design: name with a small avatar, then "Donated" and the amount
                <Box key={donor.id ?? `${donor.name}-${i}`} sx={{ display: "flex", flexDirection: "column", gap: "7px", minHeight: "106px", p: "12px", border: "1px solid #EFE4D3", borderRadius: "14px", background: "#FFFFFF", boxSizing: "border-box", minWidth: 0 }}>
                  <Box sx={{ display: "flex", gap: "7px", alignItems: "center", minWidth: 0 }}>
                    <Box sx={{ width: "25px", height: "25px", borderRadius: "100px", background: "#F1EFEB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                      {donor.avatar ? (
                        <Box component="img" src={donor.avatar} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Box component="img" src={donorMobile} alt="" sx={{ width: "16px", height: "16px" }} />
                      )}
                    </Box>
                    <Typography sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: 1.5, color: T.ink, wordBreak: "break-word" }}>{donor.name || "Anonymous"}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: "10px", lineHeight: "normal", color: T.muted }}>Donated</Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "14px", lineHeight: "normal", color: T.red, whiteSpace: "nowrap" }}>{amountLabel(donor.amount, donor.currency)}</Typography>
                </Box>
              ) : (
              <Box key={donor.id ?? `${donor.name}-${i}`} sx={{ display: "flex", gap: { xs: "8px", sm: "12px" }, alignItems: "center", minHeight: "84px", px: { xs: "10px", sm: "14px" }, py: "10px", border: `1px solid ${T.sand}`, borderRadius: "14px", background: "#FFFFFF", boxSizing: "border-box", minWidth: 0 }}>
                <Box sx={{ width: { xs: "32px", sm: "40px" }, height: { xs: "32px", sm: "40px" }, borderRadius: "20px", background: "#F1EFEB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                  {donor.avatar ? (
                    <Box component="img" src={donor.avatar} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Box component="img" src={donorUser} alt="" sx={{ width: "20px", height: "20px" }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "13px", lineHeight: "17px", color: T.ink, wordBreak: "break-word" }}>{donor.name || "Anonymous"}</Typography>
                  <Box sx={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "12px", lineHeight: "16px", color: T.muted }}>Donated</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "13px", lineHeight: "17px", color: T.red, whiteSpace: "nowrap" }}>{amountLabel(donor.amount, donor.currency)}</Typography>
                  </Box>
                </Box>
              </Box>
              )
            )}
      </Box>
      {list && list.length === 0 && (
        <Typography sx={{ fontFamily: FONT, fontSize: "14px", color: T.muted, textAlign: "center" }}>
          {tab === "top" ? "Be the first to offer seva for this temple." : "No recent donations yet."}
        </Typography>
      )}
    </SectionCard>
  );
};

export default TempleDonors;
